import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import useLargeDataAnalyticsApi from "../../hooks/useLargeDataAnalyticsApi";
import { useDispatch, useSelector } from 'react-redux';
import { Tabs, Row, Col, Divider, Space, Typography, Progress, Card } from 'antd';
import { Column, Bar, Heatmap } from '@ant-design/plots';
import {stringMixin} from "../../utils/strings";
import {useAntCharts} from "../../charts/ant/useAntCharts";
import {getHierarchicalAttribution} from "../../api/timeseriesApi";
import NivoLineController from "../../charts/nivo/NivoLineController";
import useNivoLine from "../../charts/nivo/useNivoLine";
import NivoBarChart from "../../charts/nivo/NivoBarChart";
import { ResponsiveBar } from '@nivo/bar';
import PlotlyStockChart from "../../charts/Plotly/PlotlyStockChart";
const { TabPane } = Tabs;

const indexToCountryCode = {
  "^N225": "JPN",
  "^FTSE": "GBR",
  "^HSI": "CHN",
  "^STI": "SGP",
  "^NSEI": "IND",
  "^AXJO": "AUS",
  "^GSPTSE": "CAN"
};


const indexToName = {
  "^N225": "Nikkei 225",
  "^FTSE": "FTSE 100",
  "^HSI": "Hang Seng",
  "^STI": "Straits Times",
  "^NSEI": "Nifty 50",
  "^AXJO": "ASX 200",
  "^GSPTSE": "TSX Composite",
  "^VIX": "CBOE Volatility Index",
  "^VVIX": "CBOE VVIX Index",
  "^VIX9D": "CBOE 9-Day Volatility Index",
  "^SKEW": "CBOE SKEW Index",
    "^VXN": "CBOE Nasdaq Volatility Index",
    "^irx": "3-Month Treasury Bill (IRX)",
  "^fvx": "5-Year Treasury Note (FVX)",
  "^tnx": "10-Year Treasury Note (TNX)",
  "^sp600": "S&P 600 Small Cap Index",
  "^sp400": "S&P 400 Mid Cap Index",
  "^gspc": "S&P 500 Index",
  "^ixic": "Nasdaq Composite Index"
};




const FactorsStackedBarChart = ({ data }) => {
    // Transform data into the format expected by Nivo
    const transformedData = Object.entries(data).map(([category, items]) => {
        const totalValue = items.reduce((sum, item) => sum + item.value, 0);
        return items.reduce((acc, item) => {
            acc[item.name] = (item.value / totalValue) * 100; // Convert to percentage
            return acc;
        }, { category });
    });

    // Extract all unique keys (i.e., series names)
    const keys = Array.from(new Set(Object.values(data).flatMap(items => items.map(item => item.name))));

    return (
        <div style={{ height: '500px' }}>
            <ResponsiveBar
                data={transformedData}
                keys={keys}
                indexBy="category"
                layout="horizontal" // Make the chart horizontal
                margin={{ top: 50, right: 130, bottom: 50, left: 100 }}
                padding={0.3}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={{ scheme: 'nivo' }} // Adjust color scheme as needed
                borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Percentage',
                    legendPosition: 'middle',
                    legendOffset: 32,
                    format: value => `${value}%`, // Display percentages
                }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: '',
                    legendPosition: 'middle',
                    legendOffset: -40,
                }}
                label={({ id, value }) => `${id.split('_')[1]}: ${value.toFixed(1)}%`} // Round labels to 1 decimal place
                labelSkipWidth={36}
                labelSkipHeight={12}
                labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                tooltip={({ id, value }) => (
                    <strong>
                        {id.split('_')[1]}: {value.toFixed(2)}% {/* Round tooltips to 2 decimal places */}
                    </strong>
                )}
                legends={[]}
                animate={true}
                motionStiffness={90}
                motionDamping={15}
            />
        </div>
    );
};



const CumulativeContributionChart = ({data})=>{
    console.log("ORIGINAL DATA", data);
    const formattedData = useMemo(()=>{
        const memoizedData = [];
        data.forEach(item=>{
            const formattedItem = [...item.data].map(d=>[d.x, parseFloat(d.y)]);
            memoizedData.push({
                name: item.name,
                data: formattedItem
            })
        });
        return memoizedData;

    }, [data]);



    return (
        <div style={{height: 500, width: '95%'}}>
            <PlotlyStockChart timeseriesData={formattedData} />

        </div>
         );
}


const CumulativeContributions = ({data})=> {
    const groups = Object.keys(data);

    return (
        <Tabs tabPosition={'right'}>
            {groups.map((group, index) => (
                <TabPane tab={group} key={index}>
                    <CumulativeContributionChart data={data[group]}/>
                </TabPane>
            ))}
        </Tabs>
    )
}

const AttributionView = ({data})=>{
    return (
        <div className={'p-4'}>
            <Row>
                <Col span={12}>
                    <CumulativeContributionChart data={data?.charts?.cumulative_group_contribution}/>
                </Col>
                <Col span={12}>
                    <FactorsStackedBarChart data={data?.charts?.top_factors}/>
                </Col>
            </Row>
            <Divider/>
            <Row>
                <Col span={24}>
                    <CumulativeContributions data={data?.charts?.cumulative_factor_contribution}/>

                </Col>
            </Row>


        </div>
    )
}


/**
 *
 * @param tickers {Array[String]} - List of tickers to analyze
 * @returns {JSX.Element}
 * @constructor
 */
const AttributionModel = ({ tickerSymbol }) => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const attributionResults = useSelector(state => state.timeSeries.analysis?.hierarchicalAttribution);
    // If tickers prop is provided, use it; otherwise, parse from query params


    useEffect(() => {
        if (tickerSymbol) {
            dispatch(getHierarchicalAttribution({ symbol: tickerSymbol }));
        }
    }, [dispatch, tickerSymbol]);



    console.log(attributionResults);



    return (
        <div>


            {attributionResults && <AttributionView data={attributionResults}/>}
        </div>
    );
};

export default AttributionModel;
